import './MappingTable.scss';
import cn from 'classnames';
import { useCallback, useId, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  MdCheck,
  MdOpenInNew,
  MdOutlineDelete,
  MdOutlineGroupAdd,
  MdOutlinePersonAdd,
  MdOutlinePublic,
  MdOutlineSwapHoriz
} from 'react-icons/md';
import { Badge } from '@Components/Badge';
import { Button } from '@Components/Button';
import { Checkbox } from '@Components/FormCheck';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { IconButton } from '@Components/IconButton';
import { Spinner } from '@Components/Spinner';
import { BPMTable, Header, useVisibleRowSelection } from '@Components/StickyTable';
import { ConfidenceBand } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { useSetAreYouSure } from '../../../redux/modals';
import { LocationType } from '../../../utils/routing/location-constants';
import {
  displayCid,
  GroupedMappingRow,
  MappingPartition,
  MappingSource,
  MappingTabConfig,
  MAX_INLINE_CIDS,
  MAX_OVERLAY_CIDS,
  UnassignedMappingRow
} from '../client-data-mapping-constants';
import { filterReadOnlyHeaders } from '../client-data-mapping-utils';
import { truncate } from '../../../utils/text-utils';

interface CommonMappingTableProps {
  tab: MappingTabConfig;
  scope: LocationType;
  loading: boolean;
  readOnly?: boolean;
}

// Assigned and Generic partitions share the same underlying row shape (MappingRow[]) and the
// same action set (remove + reassign). They differ only in copy: Generic rows show "Client:
// Generic" instead of a real CID, and Remove/Reassign are relabeled to reflect that removing
// clears the generic mark and reassigning turns the domain into a real client mapping.
interface AssignedOrGenericMode {
  partition: typeof MappingPartition.Assigned | typeof MappingPartition.Generic;
  rows: GroupedMappingRow[];
  // Passed full rows, not just ids -- each grouped row carries every cid it's assigned to, so
  // the caller can fan out unassign calls per (item, cid) pair.
  onRemove: (rows: GroupedMappingRow[]) => Promise<void>;
  // Only rendered when the grouped row has exactly one cid (single-CID reassign is
  // unambiguous). Multi-CID rows don't offer a Reassign button -- the user removes the
  // specific client and adds a new one via the top-level Add flow instead.
  onReassign: (row: GroupedMappingRow) => void;
  // Per-row "Mark as generic" shortcut -- only rendered on the Assigned segment of an
  // allowGeneric tab (email_domain). Single-CID: reassign to GENERIC_CID. Multi-CID: unassign
  // each client then assign generic (see handleMarkGeneric). Passed to both Assigned and
  // Generic variants so the discriminant stays shared; the render check gates on
  // `!isGeneric && tab.allowGeneric`.
  onMarkGeneric: (row: GroupedMappingRow) => Promise<void>;
  // Per-row "Add additional clients" shortcut -- only rendered on the Assigned segment of a
  // many-to-many item type (!tab.singleAssignment). Opens AddClientsModal, which lets the
  // user pick more clients to attach to the row without touching its existing assignments.
  onAddClients: (row: GroupedMappingRow) => void;
  // Bulk analogue of onReassign -- opens the bulk picker modal against the checked rows. The
  // "Reassign N selected" button gates its own render on Assigned partition + every selected
  // row having exactly one cid, so multi-cid rows never reach this handler.
  onBulkReassign: (rows: GroupedMappingRow[]) => void;
  // Promotes oracle-suggested rows to is_manual = true. Accepts a list so one handler covers
  // both the per-row button (called with [row]) and the bulk "Confirm N selected" control.
  onConfirm?: (rows: GroupedMappingRow[]) => Promise<void>;
}

interface UnassignedMode {
  partition: typeof MappingPartition.Unassigned;
  rows: UnassignedMappingRow[];
  onAssign: (row: UnassignedMappingRow) => void;
  // Bulk analogue of onAssign -- opens the bulk picker modal against the checked rows. Only
  // rendered when the current selection is non-empty (the "Assign N selected" button gates
  // this).
  onBulkAssign: (rows: UnassignedMappingRow[]) => void;
  // Per-row (and bulk) "Mark as generic" shortcut. Only rendered on allowGeneric tabs
  // (slack_channel, email_domain) in the Global scope. Accepts an array so the per-row call
  // site wraps a single row as [row] and the bulk button passes the full selection.
  onMarkGeneric?: (rows: UnassignedMappingRow[]) => Promise<void>;
}

// Discriminated on `partition`. All variants render the same BPMTable shell, filter bar, and
// empty state -- the columns and row-level actions vary per variant, computed inside the
// component so the caller doesn't have to fork.
type MappingTableMode = AssignedOrGenericMode | UnassignedMode;
type MappingTableProps = CommonMappingTableProps & MappingTableMode;

// GroupedMappingRow is a superset of UnassignedMappingRow; `isManual` is present on assigned/
// generic rows only. Used by the Source column to override "system" → "manual" when a
// system-sourced channel has since been manually assigned by a user.
const isGroupedRow = (row: UnassignedMappingRow): row is GroupedMappingRow => 'isManual' in row;

const getItemId = (row: UnassignedMappingRow): number => row.itemId;

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  // Postgres timestamps arrive without timezone info; append 'Z' so the Date constructor
  // treats them as UTC, and toLocaleString() then converts correctly to the user's local zone.
  const asUtc = value.endsWith('Z') || value.includes('+') ? value : `${value}Z`;
  return new Date(asUtc).toLocaleString();
};

const CONFIDENCE_BAND_LABELS: Record<ConfidenceBand, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low'
};

const CONFIDENCE_BAND_DESIGN: Record<ConfidenceBand, (typeof Intent)[keyof typeof Intent]> = {
  high: Intent.SUCCESS,
  medium: Intent.WARNING,
  low: Intent.ERROR
};

const SOURCE_LABEL: Record<MappingSource, string> = {
  oracle: 'Oracle',
  manual: 'Manual',
  system: 'System'
};

// Badge design uses Design for brand/neutral and Intent for warning/success
type BadgeDesign = (typeof Design)[keyof typeof Design] | (typeof Intent)[keyof typeof Intent];

const SOURCE_DESIGN: Record<MappingSource, BadgeDesign> = {
  oracle: Design.BRAND,
  manual: Intent.WARNING,
  system: Design.NEUTRAL
};

const SYNCED_SOURCES = new Set<MappingSource>(['oracle', 'system']);

const COMMENT_MAX_LENGTH = 60;

// Actions-column width = one button slot per possible action + one spacing gap between
// each pair. Every header builder derives width from these two constants so adding or
// removing an affordance in one branch can't desync from another.
const ACTIONS_COLUMN_SPACING = 4;
const ACTIONS_COLUMN_BUTTON_WIDTH = 32;
const actionsColumnWidth = (buttonCount: number) =>
  ACTIONS_COLUMN_BUTTON_WIDTH * buttonCount + ACTIONS_COLUMN_SPACING * Math.max(buttonCount - 1, 0);

// ─── Header builders ────────────────────────────────────────────────────────
//
// Extracted from the `headers` useMemo body so each partition's column set lives in its own
// named function with an explicit input contract. The useMemo body then reduces to a small
// dispatcher; the deps list on it captures the union of every builder's inputs.

interface BuildAssignedOrGenericHeadersArgs {
  selectColumn: Header;
  sharedColumns: Header[];
  scope: LocationType;
  allowGeneric: boolean;
  syncLabel: string;
  isGeneric: boolean;
  onReassign: ((row: GroupedMappingRow) => void) | null;
  onAddClients: ((row: GroupedMappingRow) => void) | null;
  onMarkGeneric: ((row: GroupedMappingRow) => Promise<void>) | null;
  onConfirm: ((rows: GroupedMappingRow[]) => Promise<void>) | null;
  confirmAndRemove: (rows: GroupedMappingRow[]) => Promise<void>;
}

// Assigned/Generic layout: bulk-select + optional Client column + shared name/source/audit
// columns + row-action stack (Reassign / Add-clients / Mark-generic / Remove)
// sized to the widest possible action set for the current partition.
const buildAssignedOrGenericHeaders = ({
  selectColumn,
  sharedColumns,
  scope,
  allowGeneric,
  syncLabel,
  isGeneric,
  onReassign,
  onAddClients,
  onMarkGeneric,
  onConfirm,
  confirmAndRemove
}: BuildAssignedOrGenericHeadersArgs): Header[] => {
  const cols: Header[] = [selectColumn];
  if (scope === LocationType.Global) {
    cols.push({
      label: 'Client(s)',
      name: 'cid',
      flex: 1,
      sortPriority: 0,
      sortAscending: true,
      // Grouped row: render every cid the domain is assigned to. Up to MAX_INLINE_CIDS
      // render comma-separated in the cell; the overflow shows "+ N more" with the full
      // list (capped at MAX_OVERLAY_CIDS) on hover. Generic rows render as the single
      // human-readable "Generic" label -- the sentinel never gets shown verbatim.
      renderer: (row: GroupedMappingRow) => <CidCell cids={row.cids} />
    });
  }
  cols.push(...sharedColumns);
  cols.push({
    label: 'Last Modified',
    name: 'lastModified',
    flex: 1,
    nonFilterable: true,
    renderer: (row: GroupedMappingRow) => (
      <span className='mapping-table-last-modified'>
        <span>{row.lastModifiedBy || (SYNCED_SOURCES.has(row.source) ? syncLabel : '—')}</span>
        <span className='mapping-table-last-modified-date'>{formatDate(row.lastModified)}</span>
      </span>
    )
  });
  // "Mark as generic" shortcut is only offered on the Assigned segment of an allowGeneric
  // tab (slack_channel, email_domain). Generic rows already have "Unmark as generic" wired
  // to the delete button below, so a Mark-as-generic button would be redundant there.
  const canShowMarkGeneric =
    scope === LocationType.Global && allowGeneric && !isGeneric && Boolean(onMarkGeneric);
  // Reassign is per-cid, so it only makes sense when the grouped row has exactly one
  // client (see the docstring on AssignedOrGenericMode.onReassign). Multi-cid rows show
  // Add-clients + Mark-as-generic + Remove; the user manages individual clients via those.
  const canShowReassignPerRow = scope === LocationType.Global && Boolean(onReassign);
  // "Add additional clients" is only meaningful on many-to-many types and on the Assigned
  // partition -- Generic rows can't have real-cid assignments (mutex).
  const canShowAddClients = scope === LocationType.Global && !isGeneric && Boolean(onAddClients);
  // Confirm is global-scope, assigned partition only -- promotes an oracle-suggested row to
  // is_manual = true. Generic rows are excluded: the assign endpoint would fire against
  // GENERIC_CID and the optimistic update writes to the wrong state slice.
  const canShowConfirm = !isGeneric && scope === LocationType.Global && Boolean(onConfirm);
  const maxActionsButtonCount =
    (canShowReassignPerRow ? 1 : 0) +
    (canShowAddClients ? 1 : 0) +
    (canShowMarkGeneric ? 1 : 0) +
    (canShowConfirm ? 1 : 0) +
    1;
  cols.push({
    label: '',
    name: 'actions',
    width: actionsColumnWidth(maxActionsButtonCount),
    nonInteractive: true,
    nonFilterable: true,
    renderer: (row: GroupedMappingRow) => {
      const showReassignForThisRow = canShowReassignPerRow && row.cids.length === 1;
      const removeTooltip = (() => {
        if (isGeneric) return 'Unmark as generic';
        if (row.cids.length > 1) return `Unassign from all ${row.cids.length} clients`;
        return 'Unassign this mapping';
      })();
      return (
        <div className='mapping-table-actions'>
          {canShowConfirm &&
            onConfirm &&
            (!row.isManual && row.source === 'oracle' ? (
              <IconButton
                data-testid='confirm-mapping-btn'
                appearance={Appearance.EMPTY}
                tooltip={{ text: 'Confirm Oracle assignment' }}
                onClick={() => onConfirm([row])}
              >
                <MdCheck />
              </IconButton>
            ) : (
              <span className='mapping-table-actions-spacer' aria-hidden='true' />
            ))}
          {canShowReassignPerRow &&
            (showReassignForThisRow && onReassign ? (
              <IconButton
                data-testid='reassign-mapping-btn'
                appearance={Appearance.EMPTY}
                tooltip={{ text: isGeneric ? 'Assign to a client' : 'Reassign to another client' }}
                onClick={() => onReassign(row)}
              >
                <MdOutlineSwapHoriz />
              </IconButton>
            ) : (
              <span className='mapping-table-actions-spacer' aria-hidden='true' />
            ))}
          {canShowAddClients && onAddClients && (
            <IconButton
              data-testid='add-clients-mapping-btn'
              appearance={Appearance.EMPTY}
              tooltip={{ text: 'Add additional clients' }}
              onClick={() => onAddClients(row)}
            >
              <MdOutlineGroupAdd />
            </IconButton>
          )}
          {canShowMarkGeneric && onMarkGeneric && (
            <IconButton
              data-testid='mark-generic-mapping-btn'
              appearance={Appearance.EMPTY}
              tooltip={{ text: 'Mark as generic' }}
              onClick={() => onMarkGeneric(row)}
            >
              <MdOutlinePublic />
            </IconButton>
          )}
          <IconButton
            data-testid='remove-mapping-btn'
            appearance={Appearance.EMPTY}
            tooltip={{ text: removeTooltip }}
            onClick={() => confirmAndRemove([row])}
          >
            <MdOutlineDelete />
          </IconButton>
        </div>
      );
    }
  });
  return cols;
};

interface BuildUnassignedHeadersArgs {
  selectColumn: Header;
  sharedColumns: Header[];
  allowGeneric: boolean;
  onAssign: ((row: UnassignedMappingRow) => void) | null;
  onMarkGeneric: ((rows: UnassignedMappingRow[]) => Promise<void>) | null;
}

// Unassigned layout: bulk-select + shared name/source columns + Assign action per row. On
// allowGeneric tabs (slack_channel, email_domain), a "Mark as generic" button appears alongside
// Assign. No Client column since there's no cid to display yet.
const buildUnassignedHeaders = ({
  selectColumn,
  sharedColumns,
  allowGeneric,
  onAssign,
  onMarkGeneric
}: BuildUnassignedHeadersArgs): Header[] => {
  const canShowMarkGeneric = allowGeneric && Boolean(onMarkGeneric);
  return [
    selectColumn,
    ...sharedColumns,
    {
      label: '',
      name: 'actions',
      width: actionsColumnWidth(canShowMarkGeneric ? 2 : 1),
      nonInteractive: true,
      nonFilterable: true,
      renderer: (row: UnassignedMappingRow) => (
        <div className='mapping-table-actions'>
          <IconButton
            data-testid='assign-mapping-btn'
            appearance={Appearance.EMPTY}
            tooltip={{ text: 'Assign to client(s)' }}
            onClick={() => onAssign?.(row)}
          >
            <MdOutlinePersonAdd />
          </IconButton>
          {canShowMarkGeneric && onMarkGeneric && (
            <IconButton
              data-testid='mark-generic-unassigned-btn'
              appearance={Appearance.EMPTY}
              tooltip={{ text: 'Mark as generic' }}
              onClick={() => onMarkGeneric([row])}
            >
              <MdOutlinePublic />
            </IconButton>
          )}
        </div>
      )
    }
  ];
};

// Common props destructured in the signature; modeProps carries the discriminated union so
// `modeProps.partition === MappingPartition.Assigned` still narrows the rest of the shape.
// Individual handlers get pulled out into local consts below -- otherwise useMemo/useCallback
// would depend on the whole `modeProps` object literal (fresh every render), which would
// recompute `headers` each render and thrash BPMTable's internal column/sort state.
export const MappingTable = ({
  tab,
  scope,
  loading,
  readOnly = false,
  ...modeProps
}: MappingTableProps) => {
  // Per-type UI behavior lives on the tab config; destructure for shorthand access.
  const {
    label: tabLabel,
    hasCallDate,
    allowGeneric,
    showExternalIdColumn,
    getExternalUrl,
    externalLinkLabel = 'Open external link',
    renderNameCell,
    nameColumnLabel = 'Name',
    externalIdColumnLabel = 'External ID',
    syncLabel = 'Oracle sync'
  } = tab;
  const setAreYouSure = useSetAreYouSure();
  // Bulk-select is only exposed on the Assigned partition. State stays here regardless (a Set
  // sitting idle on Unassigned costs nothing) so hooks stay unconditional. Keyed by the parent
  // on tab.key (remounts on tab switch), so this never needs to reset itself.
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Extract per-partition props as stable individual references. `partition`, `rows`, and each
  // handler are stable across renders (rows/handlers arrive memoized from the parent), so
  // depending on them individually keeps the hooks below from thrashing.
  //
  // `isAssignedOrGeneric` is aliased from the discriminant check -- TS control-flow analysis
  // for aliased conditions preserves the union narrowing on `modeProps` through the const, so
  // React Compiler / react-hooks lint still see the branch as a plain discriminant check.
  const { partition, rows } = modeProps;
  const isGeneric = partition === MappingPartition.Generic;
  const isAssignedOrGeneric =
    modeProps.partition === MappingPartition.Assigned ||
    modeProps.partition === MappingPartition.Generic;
  const onRemove = isAssignedOrGeneric ? modeProps.onRemove : null;
  const onReassign = isAssignedOrGeneric ? modeProps.onReassign : null;
  const onMarkGeneric = isAssignedOrGeneric ? modeProps.onMarkGeneric : null;
  const onAddClients = isAssignedOrGeneric ? modeProps.onAddClients : null;
  const onConfirm = isAssignedOrGeneric ? (modeProps.onConfirm ?? null) : null;
  const onAssign = modeProps.partition === MappingPartition.Unassigned ? modeProps.onAssign : null;
  const onMarkGenericUnassigned =
    modeProps.partition === MappingPartition.Unassigned ? (modeProps.onMarkGeneric ?? null) : null;

  const { toggleSelected, onFilteredDataChange, headersRenderer } = useVisibleRowSelection<
    GroupedMappingRow | UnassignedMappingRow,
    number
  >({ rows, getId: getItemId, selected, onSelectedChange: setSelected });

  // Drops the given itemIds from `selected`. Called at commit time on any mutation that
  // removes rows from the current partition -- if the mutation succeeds the rows are gone
  // (optimistically) and the checkboxes shouldn't linger; if it fails, optimisticMutate reverts
  // the row list and triggers a refetch, so the true state is what the user sees and re-
  // selecting is trivial. Better UX than leaving a "Unassign 21 selected" button hovering over
  // an empty table for the duration of the API round-trip.
  const clearSelection = useCallback((itemIds: number[]) => {
    setSelected(prev => {
      const next = new Set(prev);
      itemIds.forEach(id => next.delete(id));
      return next;
    });
  }, []);

  const confirmAndRemove = useCallback(
    async (targetRows: GroupedMappingRow[]) => {
      if (!onRemove) return;
      const totalPairings = targetRows.reduce((sum, r) => sum + r.cids.length, 0);
      const noun = targetRows.length === 1 ? 'this domain' : `${targetRows.length} domains`;
      const genericTitle = `Unmark ${noun} as generic?`;
      const genericMessage =
        "It will move back to the Unassigned list. This won't affect any client's mappings.";
      // Assigned copy differs slightly when the selection spans multi-cid rows -- calling
      // out how many pairings are actually going away makes the blast radius obvious.
      const assignedTitle =
        totalPairings === 1
          ? 'Unassign this mapping?'
          : `Unassign ${totalPairings} mappings across ${noun}?`;
      const assignedMessage =
        "It will move to the Unassigned list on the Global Admin page. This won't affect any other clients' mappings.";
      try {
        await setAreYouSure({
          title: isGeneric ? genericTitle : assignedTitle,
          message: isGeneric ? genericMessage : assignedMessage,
          okayText: isGeneric ? 'Unmark generic' : 'Unassign'
        });
      } catch {
        return; // user cancelled
      }
      // Clear the selection BEFORE awaiting the mutation so the bulk-action toolbar disappears
      // in the same paint as the optimistic apply() that empties the rows. If the mutation
      // fails, optimisticMutate reverts and refetches, so the user sees the true row list --
      // they can re-select if they want to retry.
      clearSelection(targetRows.map(row => row.itemId));
      await onRemove(targetRows);
    },
    [onRemove, setAreYouSure, isGeneric, clearSelection]
  );

  // Shared columns render off UnassignedMappingRow -- every field they touch (displayName,
  // callDate, source, syncedAt) exists on both row types since MappingRow extends
  // UnassignedMappingRow. The Name column may be overridden per-type via
  // MappingTabConfig.renderNameCell (email_domain wraps its value in a DomainUsersTooltip).
  const sharedColumns = useMemo<Header[]>(() => {
    const cols: Header[] = [
      {
        label: nameColumnLabel,
        name: 'displayName',
        flex: 1.5,
        ...(renderNameCell ? { renderer: renderNameCell } : {})
      }
    ];
    if (showExternalIdColumn) {
      cols.push({
        label: externalIdColumnLabel,
        name: 'externalId',
        flex: 1.5
      });
    }
    if (hasCallDate) {
      cols.push({
        label: 'Call Date',
        name: 'callDate',
        flex: 1,
        renderer: (row: UnassignedMappingRow) => formatDate(row.callDate)
      });
    }
    if (getExternalUrl) {
      cols.push({
        label: '',
        name: 'externalLink',
        width: ACTIONS_COLUMN_BUTTON_WIDTH,
        nonInteractive: true,
        nonFilterable: true,
        renderer: (row: UnassignedMappingRow) => (
          <a
            href={getExternalUrl(row.externalId)}
            target='_blank'
            rel='noopener noreferrer'
            className='mapping-table-external-link'
            aria-label={externalLinkLabel}
            onClick={e => e.stopPropagation()}
          >
            <MdOpenInNew size={20} />
          </a>
        )
      });
    }
    cols.push({
      label: 'Source',
      name: 'source',
      width: 64,
      nonFilterable: true,
      renderer: (row: UnassignedMappingRow) => {
        const confirmed = isGroupedRow(row) && row.isManual && SYNCED_SOURCES.has(row.source);
        return (
          <Badge
            size={Size.SM}
            design={confirmed ? Intent.SUCCESS : SOURCE_DESIGN[row.source]}
            leadingElement={confirmed ? <MdCheck /> : undefined}
          >
            {confirmed ? 'Confirmed' : SOURCE_LABEL[row.source]}
          </Badge>
        );
      }
    });
    cols.push({
      label: 'Confidence',
      name: 'confidenceBand',
      width: 80,
      renderer: (row: UnassignedMappingRow) =>
        row.confidenceBand ? (
          <Badge size={Size.SM} design={CONFIDENCE_BAND_DESIGN[row.confidenceBand]}>
            {CONFIDENCE_BAND_LABELS[row.confidenceBand]}
          </Badge>
        ) : (
          <span className='mapping-table-na'>—</span>
        )
    });
    cols.push({
      label: 'Comment from Oracle',
      name: 'comment',
      flex: 1,
      renderer: (row: UnassignedMappingRow) =>
        row.comment ? (
          <TruncatedComment comment={row.comment} />
        ) : (
          <span className='mapping-table-na'>—</span>
        )
    });
    return cols;
  }, [
    hasCallDate,
    showExternalIdColumn,
    getExternalUrl,
    externalLinkLabel,
    renderNameCell,
    nameColumnLabel,
    externalIdColumnLabel
  ]);

  const headers = useMemo<Header[]>(() => {
    // Select-checkbox column, used identically on Assigned/Generic (GroupedMappingRow) and
    // Unassigned (UnassignedMappingRow). Both row shapes carry itemId + displayName,
    // so the renderer types down to the common shape.
    const selectColumn: Header = {
      label: '',
      name: 'select',
      width: ACTIONS_COLUMN_BUTTON_WIDTH,
      nonInteractive: true,
      nonFilterable: true,
      renderer: (row: GroupedMappingRow | UnassignedMappingRow) => (
        <Checkbox
          checked={selected.has(row.itemId)}
          onChange={() => toggleSelected(row.itemId)}
          aria-label={`Select ${row.displayName}`}
        />
      )
    };
    const built = isAssignedOrGeneric
      ? buildAssignedOrGenericHeaders({
          selectColumn,
          sharedColumns,
          scope,
          allowGeneric,
          syncLabel,
          isGeneric,
          onReassign,
          onAddClients,
          onMarkGeneric,
          onConfirm,
          confirmAndRemove
        })
      : buildUnassignedHeaders({
          selectColumn,
          sharedColumns,
          allowGeneric,
          onAssign,
          onMarkGeneric: onMarkGenericUnassigned
        });
    return filterReadOnlyHeaders(built, readOnly);
  }, [
    readOnly,
    isAssignedOrGeneric,
    scope,
    allowGeneric,
    syncLabel,
    selected,
    sharedColumns,
    toggleSelected,
    confirmAndRemove,
    onReassign,
    onMarkGeneric,
    onAddClients,
    onConfirm,
    onAssign,
    onMarkGenericUnassigned,
    isGeneric
  ]);

  const partitionEmptyText: Record<typeof partition, string> = {
    [MappingPartition.Assigned]: `No ${tabLabel.toLowerCase()} assigned yet.`,
    [MappingPartition.Unassigned]: 'Nothing unassigned.',
    [MappingPartition.Generic]: `No generic ${tabLabel.toLowerCase()} yet.`
  };
  const emptyText = partitionEmptyText[partition];
  const noRowsRenderer = useCallback(
    () => <div className='mapping-table-empty'>{loading ? <Spinner /> : emptyText}</div>,
    [loading, emptyText]
  );

  return (
    <div className='mapping-table-container'>
      <BPMTable<GroupedMappingRow | UnassignedMappingRow>
        className='mapping-table'
        headers={headers}
        data={rows}
        noRowsRenderer={noRowsRenderer}
        headersRenderer={readOnly ? undefined : headersRenderer}
        onFilteredDataChange={onFilteredDataChange}
        additionalControls={
          selected.size === 0 ? undefined : (
            <BulkControls
              selected={selected}
              scope={scope}
              isGeneric={isGeneric}
              allowGeneric={allowGeneric}
              confirmAndRemove={confirmAndRemove}
              clearSelection={clearSelection}
              onConfirm={onConfirm}
              modeProps={modeProps}
            />
          )
        }
      />
    </div>
  );
};

// Toolbar rendered above the BPMTable when at least one row is checked. Dispatch by partition;
// each partition's control set has its own sub-component so the discriminant narrowing on
// modeProps stays local to the branch that consumes it.
interface BulkControlsProps {
  selected: Set<number>;
  scope: LocationType;
  isGeneric: boolean;
  allowGeneric: boolean;
  confirmAndRemove: (rows: GroupedMappingRow[]) => Promise<void>;
  clearSelection: (itemIds: number[]) => void;
  onConfirm: ((rows: GroupedMappingRow[]) => Promise<void>) | null;
  modeProps: MappingTableMode;
}

const BulkControls = ({
  selected,
  scope,
  isGeneric,
  allowGeneric,
  confirmAndRemove,
  clearSelection,
  onConfirm,
  modeProps
}: BulkControlsProps) => {
  if (modeProps.partition === MappingPartition.Unassigned) {
    return (
      <UnassignedBulkControls
        selected={selected}
        allowGeneric={allowGeneric}
        clearSelection={clearSelection}
        modeProps={modeProps}
      />
    );
  }
  return (
    <AssignedOrGenericBulkControls
      selected={selected}
      scope={scope}
      isGeneric={isGeneric}
      confirmAndRemove={confirmAndRemove}
      clearSelection={clearSelection}
      onConfirm={onConfirm}
      modeProps={modeProps}
    />
  );
};

interface UnassignedBulkControlsProps {
  selected: Set<number>;
  allowGeneric: boolean;
  clearSelection: (itemIds: number[]) => void;
  modeProps: Extract<MappingTableMode, { partition: typeof MappingPartition.Unassigned }>;
}

// Unassigned partition: "Assign N selected" bulk picker + optional "Mark N as generic" for
// allowGeneric tabs (slack_channel, email_domain).
const UnassignedBulkControls = ({
  selected,
  allowGeneric,
  clearSelection,
  modeProps
}: UnassignedBulkControlsProps) => {
  const unassignedSelected = modeProps.rows.filter(row => selected.has(row.itemId));
  const selectedItemIds = unassignedSelected.map(row => row.itemId);
  const { onMarkGeneric } = modeProps;
  const canMarkGeneric = allowGeneric && Boolean(onMarkGeneric);
  return (
    <>
      {canMarkGeneric && onMarkGeneric && (
        <Button
          size={Size.LG}
          appearance={Appearance.OUTLINED}
          onClick={() => {
            clearSelection(selectedItemIds);
            onMarkGeneric(unassignedSelected);
          }}
        >
          Mark {selected.size} as generic
        </Button>
      )}
      <Button
        size={Size.LG}
        onClick={() => {
          clearSelection(selectedItemIds);
          modeProps.onBulkAssign(unassignedSelected);
        }}
      >
        Assign {selected.size} selected
      </Button>
    </>
  );
};

interface AssignedOrGenericBulkControlsProps {
  selected: Set<number>;
  scope: LocationType;
  isGeneric: boolean;
  confirmAndRemove: (rows: GroupedMappingRow[]) => Promise<void>;
  clearSelection: (itemIds: number[]) => void;
  onConfirm: ((rows: GroupedMappingRow[]) => Promise<void>) | null;
  modeProps: Extract<
    MappingTableMode,
    { partition: typeof MappingPartition.Assigned | typeof MappingPartition.Generic }
  >;
}

// Assigned/Generic partition: (on Global-Admin Assigned with all-single-cid rows) outlined
// "Reassign N selected", plus a destructive Unassign/Unmark with error intent.
const AssignedOrGenericBulkControls = ({
  selected,
  scope,
  isGeneric,
  confirmAndRemove,
  clearSelection,
  onConfirm,
  modeProps
}: AssignedOrGenericBulkControlsProps) => {
  const groupedSelected = modeProps.rows.filter(row => selected.has(row.itemId));
  const confirmableSelected = groupedSelected.filter(
    row => row.source === 'oracle' && !row.isManual
  );
  const canBulkReassign =
    modeProps.partition === MappingPartition.Assigned &&
    scope === LocationType.Global &&
    groupedSelected.every(row => row.cids.length === 1);
  return (
    <>
      {onConfirm && confirmableSelected.length > 0 && (
        <Button
          size={Size.LG}
          appearance={Appearance.OUTLINED}
          onClick={() => {
            clearSelection(groupedSelected.map(row => row.itemId));
            onConfirm(confirmableSelected);
          }}
        >
          Confirm {confirmableSelected.length} selected
        </Button>
      )}
      {canBulkReassign && (
        <Button
          size={Size.LG}
          appearance={Appearance.OUTLINED}
          onClick={() => {
            clearSelection(groupedSelected.map(row => row.itemId));
            modeProps.onBulkReassign(groupedSelected);
          }}
        >
          Reassign {selected.size} selected
        </Button>
      )}
      <Button
        size={Size.LG}
        appearance={Appearance.OUTLINED}
        design={Intent.ERROR}
        onClick={() => confirmAndRemove(groupedSelected)}
      >
        {isGeneric ? 'Unmark' : 'Unassign'} {selected.size} selected
      </Button>
    </>
  );
};

interface CidCellProps {
  cids: string[];
}

interface TruncatedCommentProps {
  comment: string;
}

const TruncatedComment = ({ comment }: TruncatedCommentProps) => {
  const tooltipId = useId();
  const displayed = truncate(comment, COMMENT_MAX_LENGTH);
  const isTruncated = displayed !== comment;
  return (
    <OverlayTrigger placement='top' overlay={<Tooltip id={tooltipId}>{comment}</Tooltip>}>
      <span
        className={cn('mapping-table-comment', { 'mapping-table-comment-truncated': isTruncated })}
      >
        {displayed}
      </span>
    </OverlayTrigger>
  );
};

// Renders a grouped row's clients as up to MAX_INLINE_CIDS comma-separated CIDs, with any
// overflow shown as "+ N more" and the full list revealed on hover (capped at
// MAX_OVERLAY_CIDS). Mirrors Feature Experiments > User Experiments' user-emails column.
// Generic rows carry a single sentinel cid; displayCid translates it to "Generic".
const CidCell = ({ cids }: CidCellProps) => {
  const tooltipId = useId();
  const labels = cids.map(displayCid);
  if (labels.length <= MAX_INLINE_CIDS) {
    return <span className='mapping-table-cid-cell'>{labels.join(', ')}</span>;
  }
  const shown = labels.slice(0, MAX_INLINE_CIDS);
  const remainingCount = labels.length - shown.length;
  const overlayShown = labels.slice(0, MAX_OVERLAY_CIDS);
  const overlayRemainingCount = labels.length - overlayShown.length;
  return (
    <OverlayTrigger
      placement='right'
      overlay={
        <Tooltip id={tooltipId}>
          <div className='mapping-table-cid-cell-tooltip'>
            {overlayShown.map(cid => (
              <div key={cid}>{cid}</div>
            ))}
            {overlayRemainingCount > 0 && <div>+ {overlayRemainingCount} more</div>}
          </div>
        </Tooltip>
      }
    >
      <span className='mapping-table-cid-cell'>
        {shown.join(', ')} + {remainingCount} more
      </span>
    </OverlayTrigger>
  );
};
